import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, api } from '../lib/api';
import { AppShell } from './Layout';
import { MfaWall } from './MfaWall';
import { useSession } from '../state/session';
import { Loading } from './ui';

/** Signed-in shell with the product navigation and an org guard. */
export function AppFrame({ children, actions }: { children: ReactNode; actions?: ReactNode }) {
  const { loading, user, org, orgId } = useSession();
  // Administrators must hold a verified second factor before any tenant work,
  // so probe once and show the enrolment step instead of letting every panel
  // fail independently.
  const [gate, setGate] = useState<{ enrolled: boolean } | null>(null);
  const [checked, setChecked] = useState(false);

  const probe = useCallback(async () => {
    if (!orgId) { setChecked(true); return; }
    try {
      await api.get(`/organizations/${orgId}`);
      setGate(null);
    } catch (cause) {
      if (cause instanceof ApiError
          && (cause.code === 'mfa_required' || cause.code === 'mfa_enrollment_required')) {
        setGate({ enrolled: cause.code === 'mfa_required' });
      } else {
        setGate(null);
      }
    } finally {
      setChecked(true);
    }
  }, [orgId]);

  useEffect(() => { setChecked(false); void probe(); }, [probe]);

  const nav = [
    { to: '/app', label: 'Agreements', end: true },
    { to: '/app/templates', label: 'Templates' },
    { to: '/app/billing', label: 'Billing' },
    { to: '/app/developers', label: 'Developers' },
    { to: '/app/settings', label: 'Settings' },
  ];

  if (loading || (user && !checked)) {
    return <AppShell><div className="shell section"><Loading /></div></AppShell>;
  }
  if (!user) {
    return (
      <AppShell nav={[{ to: '/', label: 'Home' }]}>
        <div className="shell shell--narrow section">
          <div className="card stack center">
            <h1 style={{ fontSize: '1.3rem' }}>Please sign in</h1>
            <p className="muted small">Your session has ended or you are not signed in.</p>
            <Link className="btn btn--primary" to="/login">Sign in</Link>
          </div>
        </div>
      </AppShell>
    );
  }

  if (gate) {
    return <MfaWall enrolled={gate.enrolled}
                    onSatisfied={() => { setGate(null); void probe(); }} />;
  }

  return (
    <AppShell nav={nav} actions={actions ?? (
      org && <Link className="btn btn--primary btn--small" to="/app/new">New agreement</Link>
    )}>
      {children}
    </AppShell>
  );
}
